import type { StrFn } from '@duda-co/booking/runtime';
import { localizationService } from '@shared-services/localization-service';
import { useCallback, useContext } from 'react';
import { useBookingContext } from '../Context';
import strings from '../strings';
import { shouldUsePaidBooking } from '../utils';
import { BookerStateContext } from './useRuntimeCalBookingState';

const BOOKER_SCOPE = 'ui.runtimessr.booker';

export const useNativeBookerStr = (): StrFn => {
    const bookerContext = useBookingContext();
    const [bookingState] = useContext(BookerStateContext);

    const isPaidNextStep =
        shouldUsePaidBooking(bookerContext) &&
        !bookingState.rescheduleUid &&
        !bookingState.calEventData?.requiresBookerEmailVerification;

    return useCallback<StrFn>(
        (key, replacements, _prefix, fallback) => {
            // The logic when we use another cta for submit button for paid bookings.
            if (key === 'confirm' && isPaidNextStep) {
                return localizationService.str(
                    strings.paidBookingNextStepButton
                );
            }

            // A miss comes back as the scoped key itself. Answer with the
            // fallback, or the bare key: the Booker passes text the site owner
            // typed (a custom field label) through here too, and it must show
            // as typed, not as `ui.runtimessr.booker.<text>`.
            const scopedKey = `${BOOKER_SCOPE}.${key}`;
            const value = localizationService.str(
                scopedKey,
                replacements ?? undefined
            );
            return value === scopedKey ? fallback ?? key : value;
        },
        [isPaidNextStep]
    );
};
